/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import _ from 'underscore' // Npm: underscore.js library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React Icons for icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from 'components/DownloadToExcel'


/*
 * GRAPHS
 */
import CreditHistoryReadQuery from './__query__/index.credit.historyRead.query'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })

  const _QueryCreditHistoryReadVariables = React.useMemo(() => ({
      'creditId': passOn?.creditId,
      'skip': paginationModel.page * paginationModel.pageSize,
      'take': paginationModel.pageSize
  }), [paginationModel.page, paginationModel.pageSize, passOn?.creditId])
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)

  const _QueryCreditHistoryRead = useQuery(CreditHistoryReadQuery, {
    'variables': _QueryCreditHistoryReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _rowCountRef = React.useRef(0)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Data assignment.
  if (!_QueryCreditHistoryRead.loading && 0 < _QueryCreditHistoryRead.data?.CreditHistoryRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryCreditHistoryRead?.data?.CreditHistoryRead?.length || (0 < _QueryCreditHistoryRead?.data?.CreditHistoryRead?.length && !_.every(_.pluck(_QueryCreditHistoryRead?.data?.CreditHistoryRead, 'status'), j => _successFlags.includes(j)))) _isFirstLoadCompleted.current = false

  // Event handler.
  const _HandleRefresh = React.useCallback(() => {
    _QueryCreditHistoryRead.refetch()
  }, [_QueryCreditHistoryRead.refetch])
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])

  // Data assignment for DataGrid
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center'
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'lastPayment',
      'headerName': 'Last Payment',
      'description': 'Last payment limit.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
    },
    {
      'field': 'lastBalance',
      'headerName': 'Last Balance',
      'description': 'Last balance amount.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
    },
    {
      'field': 'creditedBy',
      'headerName': 'Credited By',
      'description': 'Name of person who credited.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'minWidth': 150,
      'flex': 1
    },
    {
      'field': 'creditorEmail',
      'headerName': 'Creditor Email',
      'description': 'Email of person who credited.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'minWidth': 150,
      'flex': 1
    },
    {
      'field': 'creditedAt',
      'headerName': 'Credited At',
      'description': 'Date when credit was added.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'type': 'date',
      'align': 'center',
      'headerAlign': 'center',
      'minWidth': 150,
      'flex': 1
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last updated date.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'type': 'date',
      'align': 'center',
      'headerAlign': 'center',
      'minWidth': 150,
      'flex': 1
    }
  ], [])

  const rows = React.useMemo(() => _.compact(_QueryCreditHistoryRead?.data?.CreditHistoryRead?.map((r, index) => _successFlags.includes(r?.status) || r?.id ? ({
    'id': r?.id ?? `credit-history-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'lastPayment': r?.limit?.toFixed?.(5) ?? '-',
    'lastBalance': r?.balance?.toFixed?.(5) ?? '-',
    'creditedBy': r?.creditedBy?.displayName ?? '-',
    'creditorEmail': r?.creditedBy?.email ?? '-',
    'creditedAt': r?.createdAt ? new Date(Number(r.createdAt)) : null,
    'updatedAt': r?.updatedAt ? new Date(Number(r.updatedAt)) : null,

  }) : null)), [_QueryCreditHistoryRead?.data?.CreditHistoryRead, paginationModel.page, paginationModel.pageSize])
  const _QueryCreditHistoryReadRowCount = _.first(_QueryCreditHistoryRead?.data?.CreditHistoryRead)?._totalCount
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryCreditHistoryReadRowCount && null !== _QueryCreditHistoryReadRowCount) _rowCountRef.current = _QueryCreditHistoryReadRowCount

    return _rowCountRef.current
  }, [_QueryCreditHistoryReadRowCount])

  // Return component.
  return (
    <>
      <Flex className='reportsVendor base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH='83vh'
          maxH='83vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justifyContent='flex-end' alignItems='center' gap='12px'>
            <Button
              borderRadius='8px'
              fontWeight='450'
              size='sm'
              minW='20px'
              backgroundColor='purple.200'
              color='purple'
              _hover={{ bg: 'purple.800', color: 'white' }}
              onClick={_HandleRefresh}
              isDisabled={_QueryCreditHistoryRead?.loading}
              leftIcon={<HiArrowPath />}
            >
              Refresh
            </Button>
            <DownloadToExcel
              cellsData={_QueryCreditHistoryRead?.data?.CreditHistoryRead?.map(
                (item, __index) => _successFlags.includes(item?.status) || item?.id ? ({
                  'S.No.': `${__index + (paginationModel.page * paginationModel.pageSize) + 1}.`,
                  'Last Payment': item?.limit?.toFixed?.(5) ?? '-',
                  'Last Balance': item?.balance?.toFixed?.(5) ?? '-',
                  'Credited By': item?.creditedBy?.displayName ?? '-',
                  'Creditor Email': item?.creditedBy?.email ?? '-',
                  'Credited At': item?.createdAt ? Moment(Number(item.createdAt)).format('YYYY-MM-DD hh:mm:ss A') : '-',
                  'Updated At': item?.updatedAt ? Moment(Number(item.updatedAt)).format('YYYY-MM-DD hh:mm:ss A') : '-'
                }) : {}
              )}
              headersData={[
                'S.No.',
                'Last Payment',
                'Last Balance',
                'Credited By',
                'Creditor Email',
                'Credited At',
                'Updated At'
              ].map(i => ({ 'key': i, 'label': i }))}
            />
          </Flex>

          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={rowCount}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[
              10,
              30,
              50,
              100
            ]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryCreditHistoryRead?.loading}
          />
        </Flex>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
